
/* Gifty Wholesale Kit — bundle-size variant pricing.
   Retail products are design references; only the matching kit variant
   (one cart unit) is sold. Checkout validation is still required.
*/
(() => {
  'use strict';
  const PER_PACK_CENTS = 699;
  const MIN_PACKS = 20;
  const MAX_PACKS = 200;
  const FULL_NOTICE = 'Your kit is full. You can proceed to checkout or remove a design to make changes.';
  const PAGE_SIZE = 30;

  function init(root) {
    if (root.dataset.whkReady === 'true') return;
    root.dataset.whkReady = 'true';

    const get = (selector) => root.querySelector(selector);
    const preview = get('[data-whk-preview]');
    const dialog = get('[data-whk-dialog]');
    const grid = get('[data-whk-grid]');
    const search = get('[data-whk-search]');
    const more = get('[data-whk-more]');
    const status = get('[data-whk-status]');
    const countLabel = get('[data-whk-count]');
    const totalLabel = get('[data-whk-popup-total]');
    const addCart = get('[data-whk-add-cart]');
    const message = get('[data-whk-message]');
    const collection = root.dataset.collectionHandle || '';
    const prefix = (root.dataset.rootUrl || '/').replace(/\/$/, '');
    const retailPrice = Number(root.dataset.retailPrice || 16);
    const currency = root.dataset.currency || 'USD';
    const emptySlots = Math.max(1, Math.min(12, Number(root.dataset.emptySlots) || 3));

    const variants = new Map();
    try {
      const raw = JSON.parse(get('[data-whk-parent-variants]').textContent);
      raw.forEach((variant) => {
        if (Number.isInteger(Number(variant.size))) {
          variants.set(Number(variant.size), variant);
        }
      });
    } catch (error) {
      console.error('Wholesale Kit size variants could not be loaded.', error);
    }

    const selected = new Map();
    const designs = [];
    const loadedVariantIds = new Set();
    let page = 1;
    let loadedAll = false;
    let loading = null;
    let shown = PAGE_SIZE;
    let searchingAll = false;
    let pendingSearch = null;
    let adding = false;

    const make = (tag, className, text) => {
      const el = document.createElement(tag);
      if (className) el.className = className;
      if (text !== undefined) el.textContent = text;
      return el;
    };

    const count = () => Array.from(selected.values()).reduce(
      (sum, v) => sum + v.qty, 0
    );

    const formatter = new Intl.NumberFormat(
      document.documentElement.lang || 'en-US',
      {
        style: 'currency',
        currency
      }
    );

    const money = (cents) => formatter.format(cents / 100);

    const notice = (text) => {
      message.textContent = text || '';
      message.hidden = !text;
    };

    const variantFor = (packs) => {
      const v = variants.get(packs);
      return v &&
        v.available === true &&
        Number(v.priceCents) === packs * PER_PACK_CENTS
          ? v
          : null;
    };

    const imageFor = (v, p) => {
      const img =
        v.featured_image ||
        p.image ||
        p.featured_image ||
        (p.images || [])[0];

      return typeof img === 'string'
        ? img
        : (img && (img.src || img.url)) || '';
    };

    function openPicker() {
      if (!dialog.open) {
        dialog.showModal();
      }

      if (!collection) {
        status.textContent =
          'Choose the retail nail collection in the Wholesale Kit Builder settings.';
      } else if (!designs.length && !loadedAll) {
        status.textContent = 'Loading designs…';

        loadPage()
          .then(renderGrid)
          .catch((error) => {
            status.textContent = error.message;
          });
      }
    }

    function renderPreview() {
      preview.replaceChildren();

      selected.forEach((item) => {
        const card = make('article', 'whk-selected');

        if (item.image) {
          const img = make('img');
          img.src = item.image;
          img.alt = item.sku;
          img.loading = 'lazy';
          card.append(img);
        }

        card.append(
          make('span', 'whk-selected-qty', '×' + item.qty)
        );

        card.append(
          make('div', 'whk-selected-name', item.sku)
        );

        const remove = make('button', 'whk-remove', '×');
        remove.type = 'button';

        remove.setAttribute(
          'aria-label',
          'Remove ' + item.sku
        );

        remove.addEventListener('click', () => {
          selected.delete(item.id);
          notice('');
          refresh();
        });

        card.append(remove);
        preview.append(card);
      });

      if (count() < MAX_PACKS) {
        for (let i = 0; i < emptySlots; i++) {
          const b = make('button', 'whk-empty');
          b.type = 'button';

          b.append(
            make('span', 'whk-empty-plus', '+')
          );

          b.append(
            make(
              'span',
              '',
              'Product ' + (selected.size + i + 1)
            )
          );

          b.addEventListener('click', openPicker);
          preview.append(b);
        }
      }
    }

    function add(item) {
      const old = selected.get(item.id);
      const increment = old ? 1 : 3;

      if (count() + increment > MAX_PACKS) {
        notice(
          'That addition would exceed the available quantity for this kit. Remove a pack from another design first.'
        );
        return;
      }

      if (old) {
        old.qty += 1;
      } else {
        selected.set(item.id, {
          ...item,
          qty: 3
        });
      }

      notice('');
      refresh();
    }

    function subtract(item) {
      const old = selected.get(item.id);

      if (!old) return;

      if (old.qty <= 3) {
        selected.delete(item.id);
      } else {
        old.qty -= 1;
      }

      notice('');
      refresh();
    }

    function matching() {
      const needle = search.value.trim().toLowerCase();

      return needle
        ? designs.filter(
            (d) =>
              (d.sku + ' ' + d.title)
                .toLowerCase()
                .includes(needle)
          )
        : designs;
    }

    function renderGrid() {
      const oldScroll = grid.scrollTop;
      grid.replaceChildren();

      matching().slice(0, shown).forEach((item) => {
        const card = make('article', 'whk-card');
        const existing = selected.get(item.id);

        if (existing) {
          card.classList.add('is-selected');
        }

        const imgButton = make(
          'button',
          'whk-image-button'
        );

        imgButton.type = 'button';

        imgButton.disabled =
          !item.available ||
          count() + (existing ? 1 : 3) > MAX_PACKS;

        imgButton.setAttribute(
          'aria-label',
          'Add ' + item.sku
        );

        if (item.image) {
          const img = make('img', 'whk-card-image');
          img.src = item.image;
          img.alt = item.sku;
          img.loading = 'lazy';
          imgButton.append(img);
        }

        imgButton.addEventListener(
          'click',
          () => add(item)
        );

        card.append(
          imgButton,
          make('div', 'whk-card-title', item.sku)
        );

        if (!item.available) {
          card.append(
            make('div', 'whk-sold-out', 'Sold out')
          );
        } else if (!existing) {
          const b = make(
            'button',
            'whk-card-add',
            'Add to Kit (+3)'
          );

          b.type = 'button';
          b.disabled = count() + 3 > MAX_PACKS;

          b.addEventListener(
            'click',
            () => add(item)
          );

          card.append(b);
        } else {
          const c = make('div', 'whk-controls');

          const minus = make('button', '', '−');
          minus.type = 'button';

          minus.addEventListener(
            'click',
            () => subtract(item)
          );

          const plus = make('button', '', '+');
          plus.type = 'button';
          plus.disabled = count() >= MAX_PACKS;

          plus.addEventListener(
            'click',
            () => add(item)
          );

          c.append(
            minus,
            make('strong', '', String(existing.qty)),
            plus
          );

          card.append(c);
        }

        grid.append(card);
      });

      grid.scrollTop = oldScroll;

      more.hidden =
        loadedAll && shown >= matching().length;

      if (count() === MAX_PACKS) {
        status.textContent = FULL_NOTICE;
      } else if (!designs.length && loadedAll) {
        status.textContent =
          'No eligible $16 nail designs found.';
      } else if (
        search.value.trim() &&
        !matching().length &&
        loadedAll
      ) {
        status.textContent = 'No matching designs.';
      } else if (!searchingAll) {
        status.textContent = '';
      }
    }

    function refresh() {
      renderPreview();

      const packs = count();
      const kitVariant = variantFor(packs);

      countLabel.textContent = String(packs);
      totalLabel.textContent = money(
        packs * PER_PACK_CENTS
      );

      addCart.disabled =
        adding ||
        packs < MIN_PACKS ||
        packs > MAX_PACKS ||
        !kitVariant;

      if (!adding) {
        addCart.textContent =
          'ADD WHOLESALE KIT TO CART — ' +
          money(packs * PER_PACK_CENTS);
      }

      renderGrid();

      if (
        packs === MAX_PACKS &&
        !adding &&
        (
          !message.textContent ||
          message.textContent === FULL_NOTICE
        )
      ) {
        notice(FULL_NOTICE);
      } else if (
        packs < MAX_PACKS &&
        message.textContent === FULL_NOTICE
      ) {
        notice('');
      }
    }

    async function loadPage() {
      if (loadedAll || !collection) return;
      if (loading) return loading;

      loading = (async () => {
        const url =
          prefix +
          '/collections/' +
          encodeURIComponent(collection) +
          '/products.json?limit=' +
          PAGE_SIZE +
          '&page=' +
          page;

        const res = await fetch(url, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!res.ok) {
          throw new Error(
            'Unable to load designs (HTTP ' +
            res.status +
            ').'
          );
        }

        const response = await res.json();

        const products = Array.isArray(response.products)
          ? response.products
          : [];

        for (const product of products) {
          for (const variant of (product.variants || [])) {
            if (
              Number(variant.price) !== retailPrice ||
              !String(variant.sku || '').trim()
            ) {
              continue;
            }

            if (
              !variant.id ||
              loadedVariantIds.has(String(variant.id))
            ) {
              continue;
            }

            loadedVariantIds.add(String(variant.id));

            designs.push({
              id: String(variant.id),
              sku: String(variant.sku).trim(),
              title: String(product.title || ''),
              image: imageFor(variant, product),
              available: variant.available !== false
            });
          }
        }

        page++;

        if (products.length < PAGE_SIZE) {
          loadedAll = true;
        }
      })();

      try {
        await loading;
      } finally {
        loading = null;
      }
    }

    async function loadAllForSearch() {
      if (searchingAll || loadedAll) return;

      searchingAll = true;
      status.textContent = 'Searching designs…';

      try {
        while (!loadedAll) {
          await loadPage();
        }
      } catch (error) {
        status.textContent = error.message;
      } finally {
        searchingAll = false;
        renderGrid();
      }
    }

    get('[data-whk-close]').addEventListener(
      'click',
      () => dialog.close()
    );

    get('[data-whk-done]').addEventListener(
      'click',
      () => dialog.close()
    );

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        dialog.close();
      }
    });

    more.addEventListener('click', async () => {
      if (shown < matching().length) {
        shown += PAGE_SIZE;
      } else if (!loadedAll) {
        status.textContent = 'Loading more designs…';

        try {
          await loadPage();
          shown += PAGE_SIZE;
        } catch (error) {
          status.textContent =
            'Load failed: ' + error.message;
        }
      }

      renderGrid();
    });

    search.addEventListener('input', () => {
      clearTimeout(pendingSearch);

      pendingSearch = setTimeout(async () => {
        shown = PAGE_SIZE;

        if (search.value.trim().length >= 2) {
          await loadAllForSearch();
        }

        renderGrid();
      }, 350);
    });

    addCart.addEventListener('click', async () => {
      const packs = count();
      const variant = variantFor(packs);

      if (!variant || adding) {
        notice(
          'The matching Wholesale Kit size variant is unavailable or incorrectly priced.'
        );
        return;
      }

      const properties = {
        _Bundle: 'Wholesale Kit',
        _BundleSize: String(packs),
        _BundleUniqueItems: String(selected.size)
      };

      let i = 1;

      for (const item of selected.values()) {
        properties['Design ' + i] =
          item.qty +
          ' × ' +
          item.sku +
          ' [' +
          item.id +
          ']';

        i++;
      }

      adding = true;
      notice('');
      refresh();

      addCart.textContent = 'ADDING TO CART…';

      try {
        const response = await fetch(
          root.dataset.cartAddUrl,
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: Number(variant.id),
                quantity: 1,
                properties
              }]
            })
          }
        );

        if (!response.ok) {
          const result = await response.json()
            .catch(() => ({}));

          throw new Error(
            result.description ||
            result.message ||
            'Could not add Wholesale Kit.'
          );
        }

        window.location.assign(
          root.dataset.cartUrl || '/cart'
        );
      } catch (error) {
        notice(String(error.message || error));
      } finally {
        adding = false;
        refresh();
      }
    });

    refresh();
  }

  function initAll() {
    document
      .querySelectorAll('[data-wholesale-kit]')
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initAll
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    initAll
  );
})();
